import { CheckoutScreenTheme, EFeatureFlag, FeatureFlags, GetBootResponse } from '../contracts';
import { Theme } from '../types/theme';
import { verifyAndDecodeJWT } from './boot-jwt';
import { getBootLocalStorageKeyBySuffix } from './get-boot-local-storage-key';
import { getCheckoutToken } from './get-checkout-token';
import { getHostDomain } from './get-host-domain';
import { localStorageUtil } from './local-storage-util';

const defaultTheme: Theme = {
  button: {
    color: '#7122FF',
    textColor: '#FFF',
  },
  text: '#000',
  primary: '#CACBD4',
  logo: '',
  backgroundImage: '',
  mobileBackgroundImage: '',
};

export const defaultFeatureFlags: FeatureFlags = {
  [EFeatureFlag.CHECKOUT_DISABLE_SUCCESS_SCREEN]: true,
};

const getBootFromLocalStorageBySuffix = (suffix?: string) => {
  if (!suffix) return null;

  const key = getBootLocalStorageKeyBySuffix(suffix);
  if (!key) return null;

  return localStorageUtil.get(key);
};

const getInitialBootFromLocalStorage = (): GetBootResponse | null => {
  const bootByCheckoutToken = getBootFromLocalStorageBySuffix(getCheckoutToken());
  if (bootByCheckoutToken) {
    return bootByCheckoutToken as GetBootResponse;
  }

  const bootByDomain = getBootFromLocalStorageBySuffix(getHostDomain());
  return (bootByDomain as GetBootResponse) || null;
};

const getBootValueFromHash = (): string | null => {
  const hash = window.location.hash.slice(1);
  if (!hash) return null;

  const params = new URLSearchParams(hash);
  return params.get('boot');
};

export const getBootFromURLAsync = async (): Promise<GetBootResponse | null> => {
  const bootValue = getBootValueFromHash();
  if (!bootValue) return null;
  if (bootValue.includes('.')) {
    console.info('[boot] formatDetected', { kind: 'jwt', signed: true });
    const start =
      typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    const result = await verifyAndDecodeJWT(bootValue);
    const end =
      typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    console.info('[perf][boot] ms', Math.round(end - start));
    return result;
  }
  console.error('[boot] Unsigned boot in URL is not supported; use a signed JWT. Rejecting.');
  return null;
};

export const appThemeAdaptar = (checkoutTheme?: CheckoutScreenTheme): Theme => {
  if (!checkoutTheme) {
    return defaultTheme;
  }

  return {
    logo: checkoutTheme.logo,
    button: {
      color: checkoutTheme.buttonColor,
      textColor: checkoutTheme.buttonTextColor,
    },
    primary: checkoutTheme.primaryColor,
    text: checkoutTheme.textColor,
    backgroundImage: checkoutTheme.backgroundImage,
    mobileBackgroundImage: checkoutTheme.mobileBgImage || checkoutTheme.backgroundImage,
  };
};

export const getInitialConfig = () => {
  const currentBoot = getInitialBootFromLocalStorage();

  if (!currentBoot) {
    return {
      theme: defaultTheme,
      isDefaultBoot: true,
    };
  }

  const publisherData = currentBoot.publisherData;

  return {
    theme: appThemeAdaptar(currentBoot.checkoutTheme),
    publisherId: currentBoot.checkoutTheme?.publisherId,
    supportEmail: currentBoot.supportEmail,
    couponsConfiguration: currentBoot.couponsConfiguration,
    featureFlags: currentBoot.featureFlags,
    isDefaultBoot: false,
    ...(publisherData
      ? {
          publisherData: {
            companyId: publisherData.companyId,
            companyName: publisherData.companyName,
            storeTabName: publisherData.storeTabName,
            id: publisherData.id,
          },
        }
      : {}),
  };
};
